import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Mail, ArrowLeft, RefreshCw } from 'lucide-react'
import travelAgentService from '../services/travelAgentService'
import LoadingSpinner from '../components/LoadingSpinner'
import AxonallLogo from '../components/AxonallLogo'

const HERO_IMAGE =
  'https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/atlantis-the-palmjpg-1b19e1d1-b721-471b-b737-a08aea6cf0f5.jpg'

const EmailVerificationPage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [email, setEmail] = useState('')
  const [countdown, setCountdown] = useState(0)
  const navigate = useNavigate()
  const location = useLocation()

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm()

  useEffect(() => {
    const emailFromState = location.state?.email
    if (emailFromState) {
      setEmail(emailFromState)
    } else {
      toast.error('Email verification session expired. Please complete registration again.')
      setTimeout(() => navigate('/register'), 2000)
    }
  }, [location, navigate])

  useEffect(() => {
    if (countdown <= 0) return undefined
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown])

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      const response = await travelAgentService.verifyEmail(email, data.otp)
      const applicationId = response?.data?.applicationId
      navigate('/login', {
        state: {
          email,
          applicationId,
          message:
            response.message ||
            'Email verified. Your application is under review — sign in once approved.'
        }
      })
    } catch (error) {
      toast.error(error.message || 'Verification failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOTP = async () => {
    setIsResending(true)
    try {
      await travelAgentService.resendVerificationEmail(email)
      toast.success('Verification code sent successfully')
      setCountdown(60)
    } catch (error) {
      toast.error(error.message || 'Failed to resend verification code')
    } finally {
      setIsResending(false)
    }
  }

  const formatEmail = (value) => {
    if (!value) return ''
    const [username, domain] = value.split('@')
    return `${username.slice(0, 3)}***@${domain}`
  }

  return (
    <div className="h-[100dvh] max-h-[100dvh] flex flex-col lg:flex-row overflow-hidden bg-white antialiased font-poppinsRegular text-neutral-800">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-1/2 relative min-h-0 overflow-hidden shrink-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
          style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/50 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/55" />

        <div className="absolute top-6 left-6 xl:top-8 xl:left-8 z-20">
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/25 px-4 py-2.5 text-[10px] font-poppinsSemiBold uppercase tracking-[0.2em] text-white backdrop-blur-md transition-colors hover:bg-black/40 hover:border-white/30"
          >
            <ArrowLeft className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
            Back
          </button>
        </div>

        <div className="relative z-10 flex flex-1 flex-col justify-center px-10 xl:px-16 2xl:px-20 py-12">
          <div className="max-w-md rounded-3xl border border-white/15 bg-black/35 p-9 xl:p-10 shadow-2xl shadow-black/40 backdrop-blur-2xl">
            <div className="mb-6 border-b border-white/10 pb-6">
              <AxonallLogo color="text-white" size="text-3xl xl:text-4xl" />
              <p className="mt-3 text-[10px] font-poppinsSemiBold uppercase tracking-[0.35em] text-white/55">
                Axonall Reserve
              </p>
            </div>
            <p className="font-AXONALLPrimeRegular text-2xl xl:text-3xl font-medium leading-snug tracking-tight text-white">
              Almost there
            </p>
            <p className="mt-4 text-sm font-poppinsLight leading-relaxed text-white/80">
              Enter the 6-digit code we sent to your inbox. After verification, your application moves to
              review — typically within 48 hours.
            </p>
          </div>
        </div>
      </div>

      {/* Form column */}
      <div className="relative flex h-[100dvh] min-h-0 flex-1 flex-col overflow-hidden px-4 sm:px-8 lg:bg-gradient-to-b lg:from-neutral-50 lg:via-white lg:to-neutral-100 lg:px-12 xl:px-16">
        <div className="pointer-events-none absolute inset-0 z-0 lg:hidden" aria-hidden>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
            style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-black/35 to-black/65" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-100 via-white/88 to-white/72" />
        </div>

        <div
          className="pointer-events-none absolute -right-24 top-[12%] z-0 h-72 w-72 rounded-full bg-neutral-900/[0.04] blur-3xl max-lg:opacity-40"
          aria-hidden
        />

        <div className="relative z-10 flex min-h-0 flex-1 flex-col">
          <div className="mb-4 flex shrink-0 items-center justify-between pt-6 lg:hidden">
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-2 text-[10px] font-poppinsSemiBold uppercase tracking-[0.18em] text-neutral-700 shadow-sm"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back
            </button>
            <AxonallLogo size="text-xl" color="text-neutral-900" />
          </div>

          <div className="flex min-h-0 flex-1 flex-col justify-center py-4 lg:py-6">
            <div className="mx-auto w-full max-w-md">
              <div className="mb-5 text-center lg:text-left">
                <h1 className="font-AXONALLPrimeRegular text-2xl sm:text-[1.65rem] font-medium tracking-tight text-neutral-900">
                  Verify your email
                </h1>
                <p className="mt-1.5 text-sm font-poppinsRegular text-neutral-500">
                  Enter the 6-digit code sent to your inbox
                </p>
                {email && (
                  <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-3.5 py-1.5">
                    <Mail className="h-4 w-4 text-neutral-400" aria-hidden />
                    <span className="text-sm font-poppinsMedium text-neutral-700">{formatEmail(email)}</span>
                  </div>
                )}
              </div>

              <div className="rounded-[1.5rem] border border-neutral-200/90 bg-white/95 p-6 sm:p-7 shadow-[0_20px_70px_rgba(15,23,42,0.08)] backdrop-blur-sm">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label
                      htmlFor="otp"
                      className="mb-2 block text-[11px] font-poppinsSemiBold uppercase tracking-wider text-neutral-600"
                    >
                      Verification code
                    </label>
                    <input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      className="input-field h-12 border-neutral-200 bg-white text-center text-xl font-mono tracking-[0.35em] focus:border-neutral-900/20 focus:ring-neutral-900/25"
                      placeholder="000000"
                      maxLength={6}
                      {...register('otp', {
                        required: 'Verification code is required',
                        pattern: {
                          value: /^\d{6}$/,
                          message: 'Enter a valid 6-digit code'
                        }
                      })}
                    />
                    {errors.otp && <p className="error-text">{errors.otp.message}</p>}
                    <p className="mt-2 text-[11px] font-poppinsRegular text-neutral-400">
                      Check your inbox and spam folder. The code expires in 10 minutes.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !email}
                    className="typography-button flex w-full items-center justify-center rounded-xl bg-neutral-950 py-3 text-xs font-medium tracking-[0.2em] text-white shadow-lg shadow-neutral-900/15 transition-all hover:bg-black hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isLoading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Verifying…
                      </>
                    ) : (
                      'Verify email'
                    )}
                  </button>
                </form>

                <div className="mt-5 flex flex-col items-center justify-between gap-3 border-t border-neutral-100 pt-4 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={isResending || countdown > 0 || !email}
                    className="inline-flex items-center gap-2 text-sm font-poppinsMedium text-neutral-900 underline decoration-neutral-300 underline-offset-4 transition-colors hover:decoration-neutral-900 disabled:cursor-not-allowed disabled:text-neutral-400 disabled:no-underline"
                  >
                    {isResending ? (
                      <>
                        <LoadingSpinner size="sm" />
                        Sending…
                      </>
                    ) : countdown > 0 ? (
                      `Resend in ${countdown}s`
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" aria-hidden />
                        Resend code
                      </>
                    )}
                  </button>
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-1.5 text-sm font-poppinsRegular text-neutral-500 transition-colors hover:text-neutral-800"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
                    Back to registration
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <footer className="shrink-0 pb-5 text-center lg:text-left">
            <p className="text-xs font-poppinsRegular text-neutral-500">
              © 2026 AXONALL Luxury Travel. All rights reserved.
            </p>
          </footer>
        </div>
      </div>
    </div>
  )
}

export default EmailVerificationPage
